import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { api, ApiError, setCsrfToken, type Me } from '../api.js';
import { useMeQuery, useRefreshMe } from '../auth.js';
import { Button, Card, ErrorText, Input } from '../components/ui.js';

const messages: Record<string, string> = {
  invalid_credentials: 'Email or password is wrong.',
  invalid_code: 'The code is wrong or was already used.',
  locked: 'Too many failed attempts. The account is locked for 15 minutes.',
  no_pending_login: 'The sign-in expired. Start again.',
  no_pending_enrollment: 'The sign-in expired. Start again.',
};
const describe = (e: unknown) =>
  e instanceof ApiError ? (messages[e.code] ?? (e.status === 429 ? 'Too many attempts, wait a minute.' : e.message)) : 'Network error';

export function LoginPage() {
  const me = useMeQuery();
  const refresh = useRefreshMe();
  const navigate = useNavigate();
  const done = async (next: Me) => {
    setCsrfToken(next.csrfToken);
    await refresh();
    if (next.stage === 'active') await navigate({ to: '/' });
  };

  if (me.isPending) return null;
  const stage = me.data?.stage;
  if (stage === 'totp') return <CodeStep onDone={done} />;
  if (stage === 'enroll') return <EnrollStep onDone={done} />;
  return <PasswordStep onDone={done} />;
}

function PasswordStep({ onDone }: { onDone: (me: Me) => Promise<void> }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const login = useMutation({ mutationFn: () => api<Me>('POST', '/api/auth/login', { email, password }), onSuccess: onDone });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    login.mutate();
  };
  return (
    <Card title="Sign in">
      <form onSubmit={submit} className="space-y-3">
        <Input label="Email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
        <Input label="Password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        {login.isError && <ErrorText>{describe(login.error)}</ErrorText>}
        <Button type="submit" disabled={login.isPending}>
          Continue
        </Button>
      </form>
    </Card>
  );
}

function CodeForm({ url, onDone, children }: { url: string; onDone: (me: Me) => Promise<void>; children?: React.ReactNode }) {
  const [code, setCode] = useState('');
  const verify = useMutation({ mutationFn: () => api<Me>('POST', url, { code }), onSuccess: onDone });
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        verify.mutate();
      }}
      className="space-y-3"
    >
      {children}
      <Input
        label="6-digit code from your authenticator app"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="\d{6}"
        maxLength={6}
        required
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
      />
      {verify.isError && <ErrorText>{describe(verify.error)}</ErrorText>}
      <Button type="submit" disabled={verify.isPending || code.length !== 6}>
        Verify
      </Button>
    </form>
  );
}

function CodeStep({ onDone }: { onDone: (me: Me) => Promise<void> }) {
  return (
    <Card title="Two-factor code">
      <CodeForm url="/api/auth/totp" onDone={onDone} />
    </Card>
  );
}

function EnrollStep({ onDone }: { onDone: (me: Me) => Promise<void> }) {
  const setup = useQuery({
    queryKey: ['totp-setup'],
    queryFn: () => api<{ qrDataUrl: string; secret: string }>('GET', '/api/auth/totp/setup'),
  });
  return (
    <Card title="Set up two-factor sign-in">
      {setup.isError && <ErrorText>{describe(setup.error)}</ErrorText>}
      {setup.data && (
        <CodeForm url="/api/auth/totp/enroll" onDone={onDone}>
          <p className="text-sm text-slate-600 dark:text-slate-400">Scan the code with an authenticator app, then enter the 6-digit code it shows.</p>
          <img src={setup.data.qrDataUrl} alt="TOTP QR code" className="mx-auto h-48 w-48 bg-white p-1" />
          <p className="break-all text-center font-mono text-xs">{setup.data.secret}</p>
        </CodeForm>
      )}
    </Card>
  );
}
